/**
 * Client side sorting of attendance / applicant rows inside a position.
 */
export enum AttendanceSortField {
  Created = "created",
  Name = "name",
  Rating = "rating",
  Surname = "surname",
}

/**
 * Minimal shape shared by attendance and applicant rows, so one sorter serves both tables
 */
export interface SortableAttendance {
  id: string;
  /**
   * Sign-up time of the attendance, applicants do not have it and fall back to their id
   */
  created?: Date | null;
  worker: {
    name?: string | null;
    surname?: string | null;
    workerinfo?: { rating?: number | null } | null;
  };
}

export type AttendanceSorter = <T extends SortableAttendance>(rows: readonly T[]) => T[];

const workerRating = (row: SortableAttendance): number => Number(row.worker.workerinfo?.rating ?? 0);

// applicant rows carry no created timestamp, their ids are sequential so they order the same way
const createdOrder = (row: SortableAttendance): number =>
  row.created instanceof Date ? row.created.getTime() : Number(row.id);

/**
 * Comparators for one locale, text is compared by the collation rules of that locale
 */
const comparators = (
  locale: string
): Record<AttendanceSortField, (first: SortableAttendance, second: SortableAttendance) => number> => {
  const { compare } = new Intl.Collator(locale);
  return {
    [AttendanceSortField.Created]: (first, second) => createdOrder(first) - createdOrder(second),
    [AttendanceSortField.Name]: (first, second) => compare(first.worker.name ?? "", second.worker.name ?? ""),
    [AttendanceSortField.Rating]: (first, second) => workerRating(second) - workerRating(first),
    [AttendanceSortField.Surname]: (first, second) => compare(first.worker.surname ?? "", second.worker.surname ?? ""),
  };
};

/**
 * Returns a new sorted array, original order is kept when no field is selected
 */
export const sortAttendances = <T extends SortableAttendance>(
  rows: readonly T[],
  field: AttendanceSortField | null,
  locale: string
): T[] => (field === null ? [...rows] : [...rows].sort(comparators(locale)[field]));
